'use client'
import { CalendarDays, CalendarIcon } from 'lucide-react'
import React from 'react'
import {
    composeRenderProps,
    DatePicker as AriaDatePicker,
    DateRangePicker as AriaDateRangePicker,
    Dialog as AriaDialog,
    DialogProps as AriaDialogProps,
    PopoverProps as AriaPopoverProps,
} from 'react-aria-components'
import { cn } from '@workspace/ui/lib/utils'
import { parseDate, parseDateTime } from '@internationalized/date'
import { useIsMobile } from '@workspace/ui/hooks/use-mobile'
import { Button } from '@workspace/ui/components/Button'
import {
    Calendar,
    CalendarCell,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHeader,
    CalendarHeaderCell,
    CalendarHeading,
    RangeCalendar,
} from './Calendar'
import { DateInput } from '@workspace/ui/components/Datefield'
import { FieldGroup } from '@workspace/ui/components/Field'
import { Popover } from '@workspace/ui/components/Popover'

const DatePicker = AriaDatePicker
const DateRangePicker = AriaDateRangePicker

const DatePickerContent = ({
    className,
    popoverClassName,
    ...props
}: AriaDialogProps & { popoverClassName?: AriaPopoverProps['className'] }) => (
    <Popover
        placement="bottom start"
        offset={8}
        className={composeRenderProps(popoverClassName, className =>
            cn('w-auto p-1 overflow-visible -translate-x-6', className),
        )}
    >
        <AriaDialog
            className={cn(
                'flex w-full flex-col space-y-4 outline-none sm:flex-row sm:space-x-4 sm:space-y-0',
                className,
            )}
            {...props}
        />
    </Popover>
)

interface BsDatePickerProps {
    value?: string
    onChange?: (value: string) => void
    defaultValue?: string
    className?: string
    minValue?: string
    maxValue?: string
    isDisabled?: boolean
    iconOnly?: boolean
    groupClassName?: string
    granularity?: 'day' | 'hour' | 'minute' | 'second'
    hideTimeZone?: boolean
    onBlur?: (...args: any[]) => void
}

// Helper: Ensure strict type consistency for React Aria to prevent re-renders
function parseStringValue(val: string | undefined | null, granularity?: string) {
    if (!val) return null
    try {
        let cleanVal = val.replace(/\[.*?\]$/, '').replace(/(\.\d{3})?Z?$/, '')

        // STRICT RULE: If granularity involves time, ALL values MUST be DateTime
        // This ensures value, minValue, and maxValue are always the same type.
        if (granularity && granularity !== 'day') {
            if (!cleanVal.includes('T')) {
                cleanVal = `${cleanVal}T00:00:00`
            }
            return parseDateTime(cleanVal)
        }

        // OTHERWISE: All values MUST be Date
        if (cleanVal.includes('T')) {
            cleanVal = cleanVal.split('T')[0] || ''
        }
        return parseDate(cleanVal)
    } catch (_) {
        return null
    }
}

function BsDatePicker({
    value: controlledValue,
    onChange: controlledOnChange,
    defaultValue,
    minValue,
    maxValue,
    className,
    isDisabled,
    groupClassName,
    iconOnly = false,
    granularity,
    hideTimeZone,
    ...props
}: BsDatePickerProps) {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<string | undefined>(defaultValue)

    const value = controlledValue ?? uncontrolledValue
    const onChange = controlledOnChange ?? setUncontrolledValue

    // Memoize parsed values to keep references stable
    const parsedValue = React.useMemo(() => parseStringValue(value, granularity), [value, granularity])
    const parsedMinValueProp = React.useMemo(() => parseStringValue(minValue, granularity), [minValue, granularity])
    const parsedMaxValue = React.useMemo(() => parseStringValue(maxValue, granularity), [maxValue, granularity])

    // KEY FIX: Prevent crash/loop when opening a task in the past (value < minValue)
    const effectiveMinValue = React.useMemo(() => {
        if (parsedValue && parsedMinValueProp) {
            try {
                // If current value is BEFORE the minValue, we must lower the minValue
                // to include the current value. Otherwise, Calendar tries to focus a disabled date.
                // This does NOT affect validation rule (which handles via react-hook-form)
                if (parsedValue.compare(parsedMinValueProp) < 0) {
                    return parsedValue
                }
            } catch (_) {
                // Ignore comparison errors
            }
        }
        return parsedMinValueProp
    }, [parsedValue, parsedMinValueProp])

    // Guarded handler to prevent unstable function ref and unnecessary calls
    const guardedHandleChange = React.useCallback(
        (newValue: any) => {
            if (!newValue) {
                if (value !== '') onChange('')
                return
            }
            const dateStr = newValue.toString()
            if (dateStr !== value) {
                onChange(dateStr)
            }
        },
        [value, onChange],
    )

    return (
        <DatePicker
            aria-label="Date Picker"
            isInvalid={(props as any)['aria-invalid']}
            className={composeRenderProps(className, className => cn('w-full', className))}
            value={parsedValue}
            onChange={guardedHandleChange}
            minValue={effectiveMinValue}
            maxValue={parsedMaxValue}
            isDisabled={isDisabled}
            granularity={granularity}
            hideTimeZone={hideTimeZone}
            {...props}
        >
            <FieldGroup
                variant={iconOnly ? 'ghost' : 'default'}
                className={composeRenderProps(groupClassName, className => cn(className))}
            >
                <Button variant="ghost" size="icon" className="-ml-1 size-6 data-focus-visible:ring-offset-0">
                    <CalendarDays aria-hidden className="size-4 text-muted-foreground" />
                </Button>
                {!iconOnly || (iconOnly && value !== undefined) ? (
                    <DateInput className={cn('flex-1', iconOnly && 'text-xs!')} variant="ghost" />
                ) : null}
            </FieldGroup>
            <DatePickerContent>
                <Calendar>
                    <CalendarHeading />
                    <CalendarGrid>
                        <CalendarGridHeader>{day => <CalendarHeaderCell>{day}</CalendarHeaderCell>}</CalendarGridHeader>
                        <CalendarGridBody>{date => <CalendarCell date={date} />}</CalendarGridBody>
                    </CalendarGrid>
                </Calendar>
            </DatePickerContent>
        </DatePicker>
    )
}

interface BsDateRangePickerValue {
    start: string | null
    end: string | null
}

/** Accepts values in the format YYYY-MM-DD, or null for "All time" */
interface DateRangeQuickOption {
    key: string
    label: React.ReactNode
    value: BsDateRangePickerValue
}

interface BsDateRangePickerProps {
    value?: BsDateRangePickerValue
    onChange?: (value: BsDateRangePickerValue) => void
    defaultValue?: BsDateRangePickerValue
    className?: string
    groupClassName?: string
    variant?: 'default' | 'unstyled'
    minValue?: string
    maxValue?: string
    isDisabled?: boolean
    quickOptions?: DateRangeQuickOption[]
    visibleMonths?: number
    placeholder?: string
}

function BsDateRangePicker({
    value: controlledValue,
    onChange: controlledOnChange,
    defaultValue,
    minValue,
    maxValue,
    className,
    groupClassName,
    isDisabled,
    quickOptions,
    visibleMonths,
    placeholder = 'Chọn khoảng thời gian',
    ...props
}: BsDateRangePickerProps) {
    const [uncontrolledValue, uncontrolledOnChange] = React.useState<BsDateRangePickerValue | undefined>(defaultValue)
    const value = controlledValue ?? uncontrolledValue
    const onChange = controlledOnChange ?? uncontrolledOnChange

    const isMobile = useIsMobile()
    const months = visibleMonths ?? (isMobile ? 1 : 2)

    // Track focused value for calendar navigation
    const [focusedValue, setFocusedValue] = React.useState<{ start: string; end: string } | null>(null)

    // Track which quick option is currently selected (independent of value equality)
    const [selectedQuickOptionKey, setSelectedQuickOptionKey] = React.useState<string | null>(null)

    // Control popover open state - only open when icon is clicked
    const [isOpen, setIsOpen] = React.useState(false)

    const activeQuickOptionKey = React.useMemo(() => {
        // Prefer explicit selection if set
        if (selectedQuickOptionKey) return selectedQuickOptionKey
        // Fallback to value-based detection for backward compatibility
        if (!value || !quickOptions?.length) return null
        const found = quickOptions.find(option => option.value.start === value.start && option.value.end === value.end)
        return found?.key ?? null
    }, [selectedQuickOptionKey, quickOptions, value])

    const handleQuickOptionSelect = (option: DateRangeQuickOption) => {
        setSelectedQuickOptionKey(option.key) // Track which option was selected
        onChange?.(option.value)
        // Set focused value to the start date to navigate calendar to the correct month
        // (skip when start is null, e.g. "All time" option)
        if (option.value.start) {
            setFocusedValue({ start: option.value.start, end: option.value.end ?? option.value.start })
        } else {
            setFocusedValue(null)
        }
    }

    // Reset selected quick option when user manually changes the range via calendar
    const handleCalendarChange = (value: { start?: any; end?: any } | null) => {
        setSelectedQuickOptionKey(null) // Clear explicit selection
        if (!value) {
            onChange?.({ start: '', end: '' })
            return
        }
        onChange?.({
            start: value?.start?.toString() ?? '',
            end: value?.end?.toString() ?? '',
        })
    }

    // Update focused value when value changes externally
    React.useEffect(() => {
        if (value?.start && value?.end) {
            setFocusedValue({ start: value.start, end: value.end })
        } else {
            setFocusedValue(null)
        }
    }, [value?.start, value?.end])

    // Format date from YYYY-MM-DD to DD/MM/YYYY
    const formatDateDisplay = (dateStr: string) => {
        if (!dateStr) return ''
        const [year, month, day] = dateStr.split('-')
        return `${day}/${month}/${year}`
    }

    // Format date range for display
    const displayText = React.useMemo(() => {
        if (value?.start && value?.end) {
            const startFormatted = formatDateDisplay(value.start)
            const endFormatted = formatDateDisplay(value.end)
            return `${startFormatted} - ${endFormatted}`
        }
        return ''
    }, [value?.start, value?.end])

    // When a quick option is active (e.g. "Tất cả thời gian") but displayText is empty,
    // show the option label instead of the generic placeholder
    const activeQuickOptionLabel = React.useMemo(() => {
        if (!activeQuickOptionKey || !quickOptions?.length) return null
        const found = quickOptions.find(o => o.key === activeQuickOptionKey)
        return found?.label ?? null
    }, [activeQuickOptionKey, quickOptions])

    const handleButtonClick = () => {
        setIsOpen(true)
    }

    // Determine if current value represents "All time" (both null)
    const isAllTime = value?.start === null && value?.end === null

    return (
        <DateRangePicker
            aria-label="Date Range Picker"
            isInvalid={(props as any)['aria-invalid']}
            className={composeRenderProps(className, className => cn('w-full', className))}
            value={
                !isAllTime && value?.start && value?.end
                    ? { start: parseDate(value.start), end: parseDate(value.end) }
                    : null
            }
            onChange={handleCalendarChange}
            minValue={minValue ? parseDate(minValue) : null}
            maxValue={maxValue ? parseDate(maxValue) : null}
            isDisabled={isDisabled}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            {...props}
        >
            <FieldGroup
                className={cn(
                    'bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-10 px-3 cursor-pointer',
                    !value?.start && !value?.end && !isAllTime && 'text-muted-foreground',
                    groupClassName,
                )}
                onClick={handleButtonClick}
            >
                {/* DateInput elements serve as trigger for popover positioning */}
                <DateInput
                    variant="ghost"
                    slot={'start'}
                    className="w-0 flex-none p-0 [&_span]:hidden pointer-events-none"
                />
                <span aria-hidden className="w-0 px-0 text-sm text-muted-foreground pointer-events-none invisible">
                    -
                </span>
                <DateInput
                    variant="ghost"
                    slot={'end'}
                    className="w-0 flex-none p-0 [&_span]:hidden pointer-events-none"
                />
                {/* Content with equal padding on both sides */}
                <div className="flex h-full items-center pointer-events-none">
                    <CalendarIcon aria-hidden className="mr-2 size-4 text-muted-foreground shrink-0" />
                    <span className="text-xs font-normal whitespace-nowrap">
                        {displayText || activeQuickOptionLabel || placeholder}
                    </span>
                </div>
            </FieldGroup>
            <DatePickerContent popoverClassName="w-[calc(100vw-2rem)] sm:w-auto translate-x-0 sm:-translate-x-6">
                <RangeCalendar
                    visibleDuration={{ months }}
                    focusedValue={
                        focusedValue?.start
                            ? parseDate(focusedValue.start)
                            : value?.start
                              ? parseDate(value.start)
                              : undefined
                    }
                    onFocusChange={focused =>
                        focused
                            ? setFocusedValue({ start: focused.toString(), end: value?.end || focused.toString() })
                            : undefined
                    }
                >
                    <div className="flex w-full flex-row items-start gap-1 sm:gap-4">
                        {quickOptions && quickOptions.length > 0 && (
                            <>
                                <div className="flex flex-col gap-1 rounded-2xl bg-muted/40 p-1 sm:p-2 w-24 sm:w-44 shrink-0">
                                    {quickOptions.map(option => (
                                        <button
                                            type="button"
                                            key={option.key}
                                            className={cn(
                                                'text-left cursor-pointer text-xs sm:text-sm font-normal rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 hover:bg-background transition-colors',
                                                activeQuickOptionKey === option.key
                                                    ? 'bg-background text-foreground shadow-sm'
                                                    : 'text-muted-foreground',
                                            )}
                                            onClick={() => handleQuickOptionSelect(option)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="block w-px bg-zinc-200 self-stretch rounded-full" />
                            </>
                        )}
                        <div className="flex w-full flex-col gap-3 min-w-0">
                            <CalendarHeading />
                            <div className="flex w-full gap-3 items-start">
                                {Array.from({ length: months }).map((_, index) => (
                                    <CalendarGrid key={index} offset={{ months: index }} className="w-full">
                                        <CalendarGridHeader>
                                            {day => (
                                                <CalendarHeaderCell className="w-auto text-[0.75rem] sm:text-[0.8rem]">
                                                    {day}
                                                </CalendarHeaderCell>
                                            )}
                                        </CalendarGridHeader>
                                        <CalendarGridBody>
                                            {date => <CalendarCell date={date} type="range" />}
                                        </CalendarGridBody>
                                    </CalendarGrid>
                                ))}
                            </div>
                        </div>
                    </div>
                </RangeCalendar>
            </DatePickerContent>
        </DateRangePicker>
    )
}

export { BsDatePicker, BsDateRangePicker, DatePicker, DatePickerContent, DateRangePicker }
export type { BsDatePickerProps, BsDateRangePickerProps, BsDateRangePickerValue, DateRangeQuickOption }
